import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { ChevronDown } from './icons/ChevronDown';
import { cn } from '../../lib/utils';
import { ComboboxListbox, type ComboboxListItem } from './ComboboxListbox';

/**
 * Minimal metro shape this combobox needs -- id (commit value) +
 * name (display + filter target). Narrower than the full `Metro`
 * type so callers that already have `{value, label}` option arrays
 * (e.g. ProvidersFilterBar) can pass them through without a cast.
 */
interface MetroLike {
  readonly id: string;
  readonly name: string;
}

interface MetroAreaComboboxProps {
  /** Currently selected metro id. Empty string =
   *  "All metro areas" (no narrowing) when `defaultOptionLabel`
   *  is provided, or no selection when it isn't. */
  readonly value: string;
  /** Fires with the picked metro id, or '' when the user picks
   *  the "All metro areas" default (if `defaultOptionLabel` set). */
  readonly onChange: (value: string) => void;
  readonly metros: ReadonlyArray<MetroLike>;
  /** Visible label above the input. Also used as the
   *  accessible name on the combobox input. Default: "Metro Area". */
  readonly label?: string;
  /** Placeholder shown when the input is empty (no value, no query). */
  readonly placeholder?: string;
  /** When set, prepends an "all" row (value='') with this label
   *  to the listbox -- e.g. "All metro areas" on Providers, where
   *  an empty selection means "don't narrow by metro". Detail
   *  page omits this since metro is the GATING selection (no data
   *  until the user picks one). */
  readonly defaultOptionLabel?: string;
  /**
   * Optional per-metro counts (2026-06-05). When provided, each
   * option renders a right-aligned CountBadge with the count. Keyed
   * by metro id (matches `MetroLike.id`).
   *
   * Mirrors the `counts` prop on `OccupationGroupCombobox`. Callers
   * that don't have counts (Detail's metro picker, Compare's metro
   * cells, etc.) omit the prop and the listbox renders without
   * badges -- existing behaviour preserved.
   *
   * `null` is treated the same as undefined (no badges) so callers
   * can pass null to mean "counts still loading; render without
   * badges for now" without a conditional render.
   *
   * A metro id missing from the map renders without a badge
   * (defensive against backend drift, e.g. the metros API ships
   * a new metro before the counts endpoint catches up).
   */
  readonly counts?: Readonly<Record<string, number>> | null;
}

type ListItem = ComboboxListItem;

/**
 * Single APG combobox (WAI-ARIA 1.2) for picking a metro area with
 * typeahead. Modeled after `OccupationGroupCombobox` -- same input +
 * dropdown shape, sharing the `ComboboxListbox` panel primitive.
 *
 * UX rationale (Mayuresh 2026-06-01 follow-up on PR #72): the Metro
 * filter is the most important narrowing control on Detail + the
 * second most on Providers. A plain `<select>` (`BrandSelect`) makes
 * users scroll through 55 metros; a typeahead lets them jump straight
 * to the one they want. Modeling on `OccupationGroupCombobox` keeps
 * the two filter-bar typeaheads visually + behaviorally identical so
 * the user doesn't learn two patterns.
 *
 * Kept as a sibling component (not a generalized <Combobox>) for the
 * same reasons documented in `OccupationGroupCombobox`: optional
 * "default option" row, item-shape coupling. A third caller would
 * justify extracting the input/dropdown shell into a primitive.
 *
 * Behavior:
 *   - Controlled by `value` (metro id) + `onChange`. The visible
 *     input value shows the selected metro's name when one is
 *     committed; while the listbox is open and the user is typing,
 *     it shows the query.
 *   - Opens on focus/click; closes on outside-click, Escape, or
 *     after a pick.
 *   - Filters by case-insensitive substring as the user types.
 *   - Optional "All metro areas" default (value='') for filter-style
 *     callers that allow no selection (Providers).
 *   - Keyboard: ArrowDown / ArrowUp navigate the highlight,
 *     Enter commits, Escape closes.
 */
export function MetroAreaCombobox({
  value,
  onChange,
  metros,
  label = 'Metro Area',
  placeholder = 'Search metro areas...',
  defaultOptionLabel,
  counts,
}: MetroAreaComboboxProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [query, setQuery] = useState('');
  /** True while the user is actively typing -- swaps the displayed
   *  input value from "selected metro name" to "raw query" so the
   *  listbox filter feels live. Reset on close + on commit. */
  const [isTyping, setIsTyping] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const reactId = useId();
  const listboxId = `metrocombobox-${reactId}-listbox`;
  const optionIdStem = `metrocombobox-${reactId}-opt`;

  // Stable sorted list (alphabetical) so the typeahead behaves
  // predictably regardless of `metros` prop ordering.
  const sortedMetros = useMemo(
    () => [...metros].sort((a, b) => a.name.localeCompare(b.name)),
    [metros],
  );

  // Flat list including the optional leading "All" default. Built
  // once per metros or counts change; query filtering happens
  // downstream in `filtered`.
  //
  // Count rendering rules (2026-06-05):
  //   - counts null/undefined: no badges anywhere (Detail, Compare,
  //     any caller without counts -- existing behaviour preserved).
  //   - counts present + metro id in the map: right-aligned
  //     CountBadge via ComboboxListbox.
  //   - counts present + metro id MISSING from the map: no badge
  //     (defensive against backend drift).
  //   - "All metros" default row never carries a badge.
  const allItems = useMemo<ReadonlyArray<ListItem>>(() => {
    const items: Array<ListItem> = [];
    if (defaultOptionLabel !== undefined) {
      items.push({ id: 'default', value: '', label: defaultOptionLabel });
    }
    for (const m of sortedMetros) {
      const c = counts ? counts[m.id] : undefined;
      items.push({
        id: m.id,
        value: m.id,
        label: m.name,
        count: typeof c === 'number' ? c : undefined,
      });
    }
    return items;
  }, [sortedMetros, defaultOptionLabel, counts]);

  const filtered = useMemo<ReadonlyArray<ListItem>>(() => {
    if (!isTyping || !query.trim()) return allItems;
    const needle = query.trim().toLowerCase();
    return allItems.filter((it) => it.label.toLowerCase().includes(needle));
  }, [allItems, isTyping, query]);

  const selectedLabel = useMemo(() => {
    if (!value) return '';
    const match = sortedMetros.find((m) => m.id === value);
    return match?.name ?? '';
  }, [sortedMetros, value]);

  // Outside click / Escape close.
  useEffect(() => {
    if (!isOpen) return;
    function onDown(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
        setIsTyping(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setIsOpen(false);
        setIsTyping(false);
      }
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [isOpen]);

  // Reset highlight to top whenever the filtered list shape changes.
  useEffect(() => {
    setHighlight(0);
  }, [query, isOpen]);

  function commit(item: ListItem) {
    onChange(item.value);
    setIsOpen(false);
    setIsTyping(false);
    setQuery('');
  }

  function handleKey(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setIsOpen(true);
      setHighlight((h) => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      if (isOpen && filtered[highlight]) {
        e.preventDefault();
        commit(filtered[highlight]!);
      }
    }
  }

  // What the input visually shows:
  //   - If the user is actively typing -> the query.
  //   - Otherwise -> the selected metro's name (or '' if none).
  const displayValue = isTyping ? query : selectedLabel;

  const activeOptionId =
    isOpen && filtered[highlight]
      ? `${optionIdStem}-${filtered[highlight]!.id}`
      : undefined;

  return (
    <div ref={containerRef}>
      <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-text-4">
        {label}
      </span>
      <div className="relative">
        {/* Magnifier */}
        <svg
          aria-hidden="true"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-text-4"
        >
          <circle cx="11" cy="11" r="7" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        <input
          ref={inputRef}
          type="text"
          value={displayValue}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsTyping(true);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          onClick={() => setIsOpen(true)}
          onKeyDown={handleKey}
          placeholder={placeholder}
          aria-label={label}
          autoComplete="off"
          role="combobox"
          aria-expanded={isOpen}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={activeOptionId}
          className={cn(
            'block w-full rounded-md border border-border bg-white py-2 pl-9 text-sm text-text-1 placeholder:text-text-4 transition hover:border-text-4 focus:border-blue focus:outline-none focus:ring-2 focus:ring-blue/20',
            value ? 'pr-14' : 'pr-9',
          )}
        />
        {/* Clear (X) button — same rationale + wiring as
            OccupationGroupCombobox (2026-07-02 Georgetown feedback:
            filters were hard to clear). Only rendered when a value
            is committed so the field looks quiet in its default
            state. */}
        {value && (
          <button
            type="button"
            aria-label={`Clear ${label}`}
            title={`Clear ${label}`}
            onMouseDown={(e) => {
              e.preventDefault();
              onChange('');
              setQuery('');
              setIsTyping(false);
              setIsOpen(false);
              inputRef.current?.focus();
            }}
            // WCAG 2.2 SC 2.5.8 Target Size (Minimum): the clear-X hit target
            // must be >= 24x24px. h-6 w-6 = 24px (was h-5 w-5 = 20px). The X
            // glyph inside stays its size; only the tappable area grows.
            className="absolute right-8 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full text-text-4 transition hover:bg-ice hover:text-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-blue/40"
          >
            <svg
              width="10"
              height="10"
              viewBox="0 0 10 10"
              aria-hidden="true"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
            >
              <path d="M1 1L9 9M9 1L1 9" />
            </svg>
          </button>
        )}
        <ChevronDown
          aria-hidden="true"
          className={cn(
            'pointer-events-none absolute right-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-text-4 transition-transform',
            isOpen && 'rotate-180',
          )}
        />

        {isOpen && (
          <ComboboxListbox
            id={listboxId}
            ariaLabel="Metro areas"
            items={filtered}
            highlight={highlight}
            selectedValue={value}
            optionIdStem={optionIdStem}
            onHover={setHighlight}
            onCommit={commit}
          />
        )}
      </div>
    </div>
  );
}
